import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";
import { MaskedLine } from "./Reveal";
import { scrollToSection } from "./scroll";

const HERO_IMG = "/photos/hearing.jpg";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden"
    >
      <div className="pointer-events-none absolute -top-32 -right-32 w-[42rem] h-[42rem] rounded-full bg-primary/10 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute top-1/2 -left-40 w-[30rem] h-[30rem] rounded-full bg-primary/5 blur-3xl" aria-hidden="true" />

      <div className="max-w-[90rem] mx-auto px-6 lg:px-12 w-full grid grid-cols-12 gap-8 items-center relative">
        <motion.div style={{ y: textY }} className="col-span-12 lg:col-span-7 relative z-10">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="overline-label flex items-center gap-2 mb-8"
            data-testid="hero-overline"
          >
            <MapPin size={14} />
            A 501(c)(3) nonprofit · Doylestown, Pennsylvania
          </motion.p>

          <h1
            className="font-serif font-light text-5xl sm:text-6xl lg:text-[5.6rem] leading-[0.95] tracking-tight text-foreground"
            data-testid="hero-heading"
          >
            <MaskedLine delay={0.25}>The next step</MaskedLine>
            <MaskedLine delay={0.4}>
              is a <em className="text-primary font-normal">place</em>
            </MaskedLine>
            <MaskedLine delay={0.55}>to call home.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-8 max-w-md text-base lg:text-lg leading-relaxed text-muted-foreground"
            data-testid="hero-subtext"
          >
            TNS Housing Corp builds inclusive, community-rooted housing where
            people with and without disabilities live as neighbors, starting
            with Adam's Way, in the heart of Doylestown.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#adams-way"
              data-testid="hero-cta-project"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection("#adams-way");
              }}
              className="inline-flex items-center rounded-full bg-primary px-8 py-3.5 text-sm font-medium text-primary-foreground transition-[background-color,transform] duration-300 hover:bg-lavender-deep hover:-translate-y-0.5"
            >
              Discover Adam's Way
            </a>
            <a
              href="#get-involved"
              data-testid="hero-cta-donate"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection("#get-involved");
              }}
              className="inline-flex items-center rounded-full border border-foreground/25 px-8 py-3.5 text-sm font-medium text-foreground transition-[background-color,border-color] duration-300 hover:border-primary hover:bg-primary/5"
            >
              Support the mission
            </a>
          </motion.div>
        </motion.div>

        <div className="col-span-12 lg:col-span-5 relative mt-14 lg:mt-0">
          <div
            className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[130%] h-[110%] rounded-full bg-primary/15 blur-3xl"
            aria-hidden="true"
          />
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.3, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative aspect-[4/3] rounded-3xl overflow-hidden ring-1 ring-foreground/10 shadow-sm"
            data-testid="hero-image-frame"
          >
            <motion.img
              src="/photos/adams-way-rendering.jpg"
              alt="Official concept rendering of Adam's Way in Doylestown Borough"
              fetchpriority="high"
              style={{ y: imgY, scale: 1.12 }}
              className="w-full h-full object-cover will-change-transform"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.3 }}
            className="absolute -bottom-6 right-2 lg:-right-8 bg-card border border-border rounded-2xl px-6 py-4 shadow-sm"
            data-testid="hero-badge"
          >
            <p className="font-serif text-3xl text-primary leading-none">41</p>
            <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mt-1">
              Inclusive homes planned
            </p>
          </motion.div>
        </div>
      </div>

      <motion.a
        href="#mission"
        data-testid="hero-scroll-indicator"
        onClick={(e) => {
          e.preventDefault();
          scrollToSection("#mission");
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-6 lg:left-12 flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-foreground transition-colors duration-300"
      >
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
        >
          <ArrowDown size={14} />
        </motion.span>
        Scroll
      </motion.a>
    </section>
  );
};

export default Hero;
